import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '../../../ui/toast';
import { DIALOG_TYPE } from '../../../constants';
import { RefreshListIcons } from '../../../icons/icons';
import {
  Button,
  ContextMenu,
  DataTable,
  Dialog,
  FilterSearch,
  MainLayout,
} from '../../../ui';
import { deleteCandidate } from '../../../utils/api_service/candidate';
import { fetchOrganizationUsers } from '../../../utils/redux_store/features/organization_user_reducer';
import { store } from '../../../utils/redux_store/store';
import { OrganizationUser } from '../../../utils/types/admin_types';
import { getColumns } from './Functions';
import { getMenuItemsForMenu } from './Functions/GetContextMenuItems';
import { useCandidatemanagement } from './Hooks';

export const CandidateManagement = () => {
  const {
    pageNumber,
    totalPages,
    loading,
    candidateList,
    onPageChange,
    handleSearchTermChange,
    setCandidateList,
    totalRecords,
  } = useCandidatemanagement();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<OrganizationUser | null>(null);
  const [deleteConfirmState, setDeleteConfirmState] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] =
    useState<OrganizationUser | null>(null);
  const handleOpenContextmenu = (
    id: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setSelectedRowId(id);
    setAnchorEl(event.currentTarget); // Capture the clicked element
    setContextMenuVisible(true);
  };

  // Function to handle action select (Edit, Delete)

  const handleOpenMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: OrganizationUser,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const openDeleteDialog = (employee: OrganizationUser) => {
    setEmployeeToDelete(employee);
    setDeleteConfirmState(true);
  };
  const contextMenuItems = getMenuItemsForMenu({
    onDelete: (row: OrganizationUser) => {
      openDeleteDialog(row); // <-- open the confirm dialog
    },
  });
  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;
    try {
      const response = await deleteCandidate(employeeToDelete.id);
      if (response.ok) {
        const updatedEmployees = candidateList.filter(
          (employee) => employee.id !== employeeToDelete.id,
        );
        setCandidateList(updatedEmployees);
        toast.success('Employee deleted.', { position: 'bottom-center' });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting employee: ' + error, {
        position: 'bottom-center',
      });
    } finally {
      setDeleteConfirmState(false);
      setEmployeeToDelete(null);
    }
  };

  return (
    <div className='w-full h-full flex flex-col gap-5'>
      <nav
          className='flex w-full justify-between items-center border-b pb-3 text-sm'
          aria-label='Breadcrumb'
      >
        <ol className='inline-flex items-center space-x-1 md:space-x-2'>
          <li>
            <Link to={'#'} className='text-primary-textGray hover:underline'>
              Settings
            </Link>
          </li>
          <li>
            <span className='text-gray-400'>/</span>
          </li>
          <li>
            <Link
                to={'#'}
                className='text-primary-main font-semibold hover:underline'
            >
              Candidate Management
            </Link>
          </li>
        </ol>
      </nav>
      <MainLayout>
        <div className='flex flex-row items-center gap-5'>
          <FilterSearch
              onSearch={handleSearchTermChange}
              searchText='Search by name, email'
          />
          <Button
              label='Refresh'
              icon={<RefreshListIcons className='w-5 h-5' />}
              onClick={() => store.dispatch(fetchOrganizationUsers())}
              variant='secondary'
              hasIcon
          />
        </div>

        <div className='relative w-full h-[calc(100dvh-260px)] min-h-[360px] min-w-0 sm:h-[calc(100vh-230px)]'>
          <DataTable
              isLoading={loading}
              columns={getColumns({
                handleOpenMenu,
                handleCloseMenu,
                anchorEl,
              })}
              data={candidateList}
              rowsPerPage={10}
              onPageChange={onPageChange}
              currentPage={pageNumber}
              totalPages={totalPages}
              totalRecords={totalRecords}
          />
        </div>
      </MainLayout>
      { anchorEl && selectedRow && (
        <ContextMenu<OrganizationUser>
            anchorEl={anchorEl}
            onClose={handleCloseMenu}
            row={selectedRow}
            menuItems={contextMenuItems}
        />
      ) }

      <Dialog
          title='Are you sure you want to delete this employee?'
          handleApply={handleDeleteEmployee}
          confirmButtonLable='Yes, Cancel'
          isOpen={deleteConfirmState}
          onClose={() => setDeleteConfirmState(false)}
          cancelButtonLable='No, Delete'
          dialogType={DIALOG_TYPE.ERROR}
          justify='end'
      >
        <p className='text-primary-textGray text-sm'>
          This action cannot be undone, changes that have you made will be lost.
        </p>
      </Dialog>
    </div>
  );
};
